import { normalizeHeader } from '../../../Clients/ImportClients/autoMap';
import { ColumnMapping } from '../../../Clients/ImportClients/autoMap';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { ClientEntity, ProfessionalEntity } from '../../../../store/entities/state';

export type RowOutcome = 'create' | 'skip' | 'error' | 'new-client';

export interface ParsedAppointmentRow {
    rowIndex: number;
    outcome: RowOutcome;
    date: string | null;       // yyyy-MM-dd
    time: string | null;       // HH:mm
    endTime: string | null;    // HH:mm (from CSV, without duration fallback)
    professionalId: string | null;
    professionalName: string | null;
    clientId: string | null;
    clientName: string | null;
    isNewClient: boolean;
    serviceKey: string | null;
    serviceLabel: string | null;
    room: string | null;        // raw CSV value; null means use resolveRoom() fallback
    price: number | null;
    notes: string | null;
    hasHappened?: boolean;
    errors: string[];
}

const DATE_DMY_RE = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/;
const DATE_YMD_RE = /^(\d{4})[\/\-](\d{2})[\/\-](\d{2})/;
const TIME_RE = /(\d{1,2}):(\d{2})/;
const COMBINED_DATETIME_RE = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})\s+(\d{1,2}):(\d{2})|^(\d{4})[\/\-](\d{2})[\/\-](\d{2}).(\d{2}):(\d{2})/;

const STATUS_HAPPENED = new Set(['concluida', 'concluída', 'faturada', 'presente', 'done', 'completed']);
const STATUS_NOT_HAPPENED = new Set(['faltou', 'no-show', 'noshow', 'ausente']);
const STATUS_CANCELLED = new Set(['cancelada', 'cancelled', 'canceled']);

export function zeroPad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

export function parseDate(raw: string): string | null {
    if (!raw) return null;
    const s = raw.trim();

    const combined = COMBINED_DATETIME_RE.exec(s);
    if (combined) {
        if (combined[1]) {
            const d = parseInt(combined[1], 10);
            const m = parseInt(combined[2], 10);
            const y = parseInt(combined[3], 10);
            return `${y}-${zeroPad(m)}-${zeroPad(d)}`;
        } else {
            return `${combined[6]}-${combined[7]}-${combined[8]}`;
        }
    }

    const dmy = DATE_DMY_RE.exec(s);
    if (dmy) {
        const d = parseInt(dmy[1], 10);
        const m = parseInt(dmy[2], 10);
        const y = parseInt(dmy[3], 10);
        return `${y}-${zeroPad(m)}-${zeroPad(d)}`;
    }

    const ymd = DATE_YMD_RE.exec(s);
    if (ymd) {
        return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
    }

    return null;
}

export function parseTime(raw: string): string | null {
    if (!raw) return null;
    const s = raw.trim();
    const m = TIME_RE.exec(s);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h >= 0 && h <= 7) h += 12;
    if (h > 23 || min > 59) return null;
    return `${zeroPad(h)}:${zeroPad(min)}`;
}

export function parseCombinedDatetime(raw: string): { date: string; time: string } | null {
    if (!raw) return null;
    const s = raw.trim();
    const m = COMBINED_DATETIME_RE.exec(s);
    if (!m) return null;
    if (m[1]) {
        const d = parseInt(m[1], 10);
        const mo = parseInt(m[2], 10);
        const y = parseInt(m[3], 10);
        const h = parseInt(m[4], 10);
        const min = parseInt(m[5], 10);
        return { date: `${y}-${zeroPad(mo)}-${zeroPad(d)}`, time: `${zeroPad(h)}:${zeroPad(min)}` };
    } else {
        return {
            date: `${m[6]}-${m[7]}-${m[8]}`,
            time: `${zeroPad(parseInt(m[9], 10))}:${zeroPad(parseInt(m[10], 10))}`,
        };
    }
}

export function parsePrice(raw: string): number | null {
    if (!raw) return null;
    const cleaned = raw.replace(/[€$£\s]/g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

function parseStatusField(raw: string): { hasHappened?: boolean; isCancelled: boolean } {
    if (!raw) return { isCancelled: false };
    const norm = raw.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (STATUS_CANCELLED.has(norm) || STATUS_CANCELLED.has(raw.trim().toLowerCase())) return { isCancelled: true };
    if (STATUS_HAPPENED.has(norm) || STATUS_HAPPENED.has(raw.trim().toLowerCase())) return { hasHappened: true, isCancelled: false };
    if (STATUS_NOT_HAPPENED.has(norm) || STATUS_NOT_HAPPENED.has(raw.trim().toLowerCase())) return { hasHappened: false, isCancelled: false };
    return { isCancelled: false };
}

export function resolveEndTime(
    startTime: string,
    endTime: string | null,
    serviceKey: string | null,
    options: ImportAppointmentOptions,
    generalSettings: ReturnType<typeof getGeneralSettings>,
): string {
    if (endTime) return endTime;
    let durationMinutes = options.defaultDurationMinutes;
    if (serviceKey) {
        const svc = generalSettings?.services?.[serviceKey];
        if (svc?.defaultDurationMinutes) durationMinutes = svc.defaultDurationMinutes;
    }
    const [h, m] = startTime.split(':').map(Number);
    const total = h * 60 + m + durationMinutes;
    return `${zeroPad(Math.floor(total / 60) % 24)}:${zeroPad(total % 60)}`;
}

export function resolveRoom(
    serviceKey: string | null,
    options: ImportAppointmentOptions,
    generalSettings: ReturnType<typeof getGeneralSettings>,
    csvRoomValue?: string | null,
): string | undefined {
    // CSV column value takes highest priority
    if (csvRoomValue) return csvRoomValue;
    if (serviceKey) {
        const rooms = generalSettings?.services?.[serviceKey]?.defaultRooms;
        if (rooms && rooms.length > 0) return rooms[0];
    }
    if (options.defaultRoom) return options.defaultRoom;
    if (generalSettings?.allowAppointmentsWithoutRoom) return undefined;
    const firstRoom = Object.keys(generalSettings?.rooms ?? {})[0];
    return firstRoom;
}

function getColValues(row: string[], colIndices: number[]): string {
    return colIndices.filter(i => i >= 0).map(i => (row[i] ?? '').trim()).filter(Boolean).join(' ');
}

export interface ParseContext {
    mapping: ColumnMapping;
    options: ImportAppointmentOptions;
    clients: ClientEntity[];
    professionals: ProfessionalEntity[];
    generalSettings: ReturnType<typeof getGeneralSettings>;
    serviceNames: Record<string, string>;
}

export function parseAppointmentRow(
    row: string[],
    rowIndex: number,
    ctx: ParseContext,
): ParsedAppointmentRow {
    const { mapping, options, clients, professionals, generalSettings, serviceNames } = ctx;

    const getCol = (field: string): string =>
        getColValues(row, mapping.fieldMap[field] ?? []);

    // Silently skip truly empty rows
    const isEmpty = row.every(cell => !cell || !cell.trim());
    if (isEmpty) {
        return {
            rowIndex, outcome: 'skip', date: null, time: null, endTime: null,
            professionalId: null, professionalName: null, clientId: null,
            clientName: null, isNewClient: false, serviceKey: null, serviceLabel: null,
            room: null, price: null, notes: null, errors: [],
        };
    }

    const errors: string[] = [];
    let outcome: RowOutcome = 'create';

    // --- Date ---
    const dateRaw = getCol('date');
    let date: string | null = null;
    let time: string | null = null;

    if (dateRaw) {
        const combined = parseCombinedDatetime(dateRaw);
        if (combined) {
            date = combined.date;
            time = combined.time;
        } else {
            date = parseDate(dateRaw);
        }
    }

    if (!date) {
        errors.push('invalidDate');
    }

    // --- Start time ---
    const startRaw = getCol('start');
    if (startRaw && !time) {
        time = parseTime(startRaw);
    }
    if (!time) {
        const openHour = generalSettings?.openningHour ?? '09:00';
        time = openHour.length >= 5 ? openHour.substring(0, 5) : openHour;
    }

    // --- End time ---
    const endRaw = getCol('end');
    let endTime: string | null = null;
    if (endRaw) {
        endTime = parseTime(endRaw);
    }

    // --- Status ---
    const statusRaw = getCol('status');
    const { hasHappened, isCancelled } = parseStatusField(statusRaw);
    if (isCancelled) outcome = 'skip';

    // --- Professional ---
    const profRaw = getCol('professional');
    let professionalId: string | null = null;
    let professionalName: string | null = null;

    if (profRaw) {
        const normProf = normalizeHeader(profRaw);
        const mapped = options.professionalMappings[normProf];
        if (mapped === 'skip') {
            outcome = 'skip';
        } else if (mapped) {
            const prof = professionals.find(p => p.id === mapped);
            professionalId = mapped;
            professionalName = prof?.name ?? mapped;
        } else {
            const directMatch = professionals.find(
                p => normalizeHeader(p.name) === normProf || p.id === profRaw,
            );
            if (directMatch) {
                professionalId = directMatch.id;
                professionalName = directMatch.name;
            } else {
                errors.push('unknownProfessional');
            }
        }
    } else {
        errors.push('noProfessional');
    }

    // --- Client ---
    const clientRaw = getCol('client');
    let clientId: string | null = null;
    let clientName: string | null = clientRaw || null;
    let isNewClient = false;

    if (clientRaw) {
        const normClient = normalizeHeader(clientRaw);
        const found = clients.find(c => {
            const normName = normalizeHeader(c.name ?? '');
            const normEmail = normalizeHeader(c.email ?? '');
            const normPhone = (c.phoneNumbers ?? []).some(p => p.replace(/\D/g, '') === clientRaw.replace(/\D/g, ''));
            return normName === normClient || normEmail === normClient || normPhone;
        });
        if (found) {
            clientId = found.id ?? null;
            clientName = found.name;
        } else if (options.createMissingClients) {
            isNewClient = true;
            if (outcome === 'create') outcome = 'new-client';
        } else {
            errors.push('clientNotFound');
        }
    } else {
        errors.push('noClient');
    }

    // --- Service / type ---
    const typeRaw = getCol('type');
    let serviceKey: string | null = null;
    let serviceLabel: string | null = null;

    if (typeRaw) {
        const normType = normalizeHeader(typeRaw);
        const mapped = options.serviceMappings[normType];
        if (mapped === 'skip') {
            outcome = 'skip';
        } else if (mapped && mapped !== 'raw') {
            serviceKey = mapped;
            serviceLabel = serviceNames[mapped] ?? mapped;
        } else {
            serviceLabel = typeRaw;
        }
    }

    // --- Room ---
    const roomRaw = getCol('room');
    const room = roomRaw || null;

    // --- Price ---
    const priceRaw = getCol('price');
    const price = priceRaw ? parsePrice(priceRaw) : null;

    // --- Notes ---
    const notesRaw = getCol('notes');
    const notes = notesRaw || null;

    if (outcome !== 'skip' && errors.length > 0) {
        outcome = 'error';
    }

    return {
        rowIndex,
        outcome,
        date,
        time,
        endTime,
        professionalId,
        professionalName,
        clientId,
        clientName,
        isNewClient,
        serviceKey,
        serviceLabel,
        room,
        price,
        notes,
        hasHappened,
        errors,
    };
}
